import { BottomSheet, DialogFooter, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { useCreateChatChannel } from "@src/appV2/Facilities/Chat/api/useCreateChatChannel";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { Button } from "@src/appV2/redesign/components/Button";
import { ButtonInternalLink } from "@src/appV2/redesign/components/ButtonInternalLink";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useCallback } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { getSentHomeRequestCreatePath } from "../../../CreateSentHomeRequest/path";

export interface TimekeepingOptionsBottomSheetProps {
  modalState: UseModalState;
  clockOutModalState: UseModalState;
  shiftId: string;
  workplaceId?: string;
}

export function TimekeepingOptionsBottomSheet(props: TimekeepingOptionsBottomSheetProps) {
  const { modalState, clockOutModalState, shiftId, workplaceId } = props;
  const history = useHistory();
  const worker = useDefinedWorker();
  const { mutateAsync: createChatChannel, isLoading: isCreatingChatChannel } =
    useCreateChatChannel();

  function handleClockOut() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_OPTIONS_CLOCK_OUT_TAPPED, { shiftId });
    clockOutModalState.openModal();
    modalState.closeModal();
  }

  function handleSentHome() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_OPTIONS_SENT_HOME_TAPPED, { shiftId });
    history.push(getSentHomeRequestCreatePath(shiftId));
    modalState.closeModal();
  }

  async function handleChatWithSupport() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_OPTIONS_CHAT_WITH_SUPPORT_TAPPED, { shiftId });
    await openZendeskMessaging();
    modalState.closeModal();
  }

  async function handleChatWithWorkplace() {
    logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_OPTIONS_CHAT_WITH_WORKPLACE_TAPPED, { shiftId });
    if (isDefined(workplaceId)) {
      await createChatChannel({
        facilityId: workplaceId,
        agentId: worker.userId,
      });

      history.push(
        generatePath(CHAT_PAGE_FULL_PATH, {
          facilityId: workplaceId,
        })
      );
      modalState.closeModal();
    }
  }

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={<DialogFooter orientation="vertical" LinkComponent={ButtonInternalLink} />}
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <Stack
        sx={{
          paddingTop: 6,
          paddingX: 4,
        }}
      >
        <Stack spacing={4}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              handleClockOut();
            }}
          >
            I need to clock out
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              handleSentHome();
            }}
          >
            I was sent home
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIconType="text-bubble"
            onClick={() => {
              void handleChatWithSupport();
            }}
          >
            Chat with support
          </Button>

          <Button
            fullWidth
            variant="outlined"
            size="large"
            startIconType="text-bubble"
            disabled={isCreatingChatChannel}
            onClick={() => {
              void handleChatWithWorkplace();
            }}
          >
            Chat with workplace
          </Button>
        </Stack>
      </Stack>
    </BottomSheet>
  );
}
